import { FileUpload as FileUploadPrimitive } from '@ark-ui/react/file-upload'
import { LineFileText } from '@qr-front/icons/line-file-text'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'

import {
	cn,
	getVersionForClone,
	resizeImageMaintainAspect,
	resizeImageToSquare,
} from '@/utils'

import { Spinner } from '../Spinner'
import { FileActions } from './FileActions'
import { FileProgress } from './FileProgress'
import { generateVideoThumbnail } from './video.utils'

const borderCls = getVersionForClone({
	base: 'rounded-10',
	clone1: 'rounded-full',
})

export const SingleFilePreview = ({
	file,
	onReplace,
	onDelete,
	deleteButtonLabel,
	showPreviewButton,
	onPreview,
	progress,
	hidePreview,
	placeholderIcon,
	disabled,
	fileNameTransformer,
	maintainAspectRatio,
}: {
	file: File
	onReplace: () => void
	onDelete: (file: File, index: number) => void
	deleteButtonLabel?: string
	showPreviewButton?: boolean
	onPreview?: () => void
	progress: number
	hidePreview?: boolean
	placeholderIcon?: React.ReactNode
	disabled?: boolean
	fileNameTransformer: ((name: string) => string) | null
	maintainAspectRatio?: boolean
}) => {
	const [previewUrl, setPreviewUrl] = useState('')
	const [isProcessing, setIsProcessing] = useState(false)
	const isProgressReady = progress >= 100

	useEffect(() => {
		setPreviewUrl('')
		setIsProcessing(false)
	}, [file])

	const canHavePreview =
		file.type.startsWith('image/') || file.type.startsWith('video/')

	useEffect(() => {
		if (!previewUrl && canHavePreview) {
			setIsProcessing(true)
			const isVideoFile = file.type.startsWith('video/')
			const isImageFile =
				file.type.startsWith('image/') || file.type === 'image/svg+xml'

			if (isVideoFile) {
				generateVideoThumbnail(file).then((thumbnailUrl) => {
					setPreviewUrl(thumbnailUrl)
					setIsProcessing(false)
				})
			} else if (isImageFile) {
				if (file.type === 'image/svg+xml') {
					setPreviewUrl(URL.createObjectURL(file))
					setIsProcessing(false)
				} else {
					const resizeFunction = maintainAspectRatio
						? resizeImageMaintainAspect
						: resizeImageToSquare
					resizeFunction(file, 312)
						.then((blob) => {
							const url = URL.createObjectURL(blob)
							setPreviewUrl(url)
							setIsProcessing(false)
						})
						.catch(() => setIsProcessing(false))
				}
			} else {
				setIsProcessing(false)
			}
		}
	}, [file, previewUrl, maintainAspectRatio, canHavePreview])

	const fileNameToDisplay = fileNameTransformer
		? fileNameTransformer(file.name)
		: file.name
	return (
		<FileUploadPrimitive.Item file={file} asChild>
			<div className="flex items-center gap-4">
				<div
					className={cn(
						'relative flex size-20 shrink-0 items-center justify-center border border-border-grey-50 bg-white',
						borderCls,
					)}
				>
					{previewUrl ? (
						<>
							<motion.img
								alt={fileNameToDisplay}
								src={previewUrl}
								className={cn(
									'size-16 transform-gpu',
									maintainAspectRatio ? 'object-contain' : 'object-cover',
									borderCls,
								)}
							/>
							{!isProgressReady && (
								<div
									className={cn(
										'absolute inset-2 z-[1] flex flex-col items-center justify-center gap-1 bg-black/50 type-body-caption text-white',
										borderCls,
									)}
								>
									{progress}%
									<div className="relative h-[8px] w-9 rounded-full bg-white/30">
										<div
											className="absolute left-0 top-0 h-full rounded-full bg-white transition-[width] duration-300 ease-out"
											style={{ width: `${Math.round((36 * progress) / 100)}px` }}
										/>
									</div>
								</div>
							)}
						</>
					) : isProcessing ? (
						<div
							className={cn(
								'flex size-16 items-center justify-center bg-light-blue',
								borderCls,
							)}
						>
							<Spinner size={32} color="blue" />
						</div>
					) : !hidePreview || !isProgressReady ? (
						<FileProgress progress={progress} small />
					) : (
						<div
							className={cn(
								'size-16 bg-light-blue p-4 text-blue [&_svg]:size-8',
								borderCls,
							)}
						>
							{placeholderIcon || <LineFileText />}
						</div>
					)}
				</div>

				<div className="flex grow flex-col items-center justify-between gap-4 text-start tablet:flex-row">
					<h5 className="type-body-medium hidden break-all font-semibold tablet:block">
						{fileNameToDisplay}
					</h5>
					<FileActions
						onPreview={onPreview}
						onReplace={onReplace}
						onDelete={() => onDelete(file, 0)}
						deleteButtonLabel={deleteButtonLabel}
						showPreviewButton={showPreviewButton}
						disabled={disabled}
					/>
				</div>
			</div>
		</FileUploadPrimitive.Item>
	)
}
